import { calculateAge } from "../lib/age";
import { getAnswerById } from "../lib/helpers";

export const questions = [
  {
    id: 'personal-info',
    text: "Let's start with the patient's full name and date of birth.",
    type: 'personal-info',
    hint: "Please fill in both fields to continue"
  },
  {
    id: 'contact-info',
    text: "Now, let's get the patient's phone number and email address.",
    type: 'contact-info',
    hint: "We only accept US phone numbers. If you are submitting on behalf of the patient, please use the phone number you would like to use for communication."
  },
  {
    id: 'sex',
    text: "What was the patient's sex assigned at birth?",
    type: 'sex',
    hint: "Please select one option to continue. This information is used to determine the patient's treatment options."
  },
  {
    id: 'treatment',
    text: "I would like treatment for:",
    type: 'treatment',
    hint: "Please select one treatment option to continue"
  },
  {
    id: 'state',
    text: "What state are you in right now?",
    type: 'state',
    hint: "Please select your state to continue. By selecting you confirm that you will be in this state for your care."
  },
  {
    id: "minor-consent",
    text: "To treat patients under 18, we require the consent of a parent or guardian.",
    type: "minor-consent",
    conditional: true,
    conditionalFn: ({ answers, questions }) => {
      const pi = getAnswerById(answers, questions, "personal-info");
      const age = calculateAge(pi?.dateOfBirth);
      return age !== null && age >= 3 && age < 18; // (<=2 handled by infant block)
    },
  },
  {
    id: "minor-solo-treatment",
    text: "Minor solo treatment consent.",
    type: "minor-solo-treatment",
    conditional: true,
    conditionalFn: ({ answers, questions }) => {
      const pi = getAnswerById(answers, questions, "personal-info");
      const age = calculateAge(pi?.dateOfBirth);
      return age !== null && age > 14 && age < 18; // 15–17
    },
  },
  {
    id: "minor-weight",
    text: "What is the minor's weight? (in pounds)",
    type: "minor-weight",
    conditional: true,
    conditionalFn: ({ answers, questions }) => {
      const pi = getAnswerById(answers, questions, "personal-info");
      const age = calculateAge(pi?.dateOfBirth);
      return age !== null && age < 18;
    },
  },
  {
    id: "parent-instructions",
    text: "Instructions to parents.",
    type: "parent-instructions",
    conditional: true,
    conditionalFn: ({ answers, questions }) => {
      const pi = getAnswerById(answers, questions, "personal-info");
      const age = calculateAge(pi?.dateOfBirth);
      return age !== null && age < 18;
    },
  },
  {
    id: "payment",
    text: "How are you paying for this consultation?",
    type: "payment",
    hint: "Please select one payment option to continue",
    conditional: true,
    conditionalFn: ({ answers, questions }) => {
      const { calculateAge } = require("../lib/age");
      const { getAnswerById } = require("../lib/helpers");
      const pi = getAnswerById(answers, questions, "personal-info");
      const age = calculateAge(pi?.dateOfBirth);
      return age !== null && age >= 18; // adults see it here
    },
  },
  {
    id: "insurance-method",
    text: "How would you like to provide your insurance information?",
    type: "insurance-method",
    optional: false,
    conditional: true,
    // REPLACE dependsOn/dependsOnValue with this:
    conditionalFn: ({ answers, questions }) => {
      const { getAnswerById } = require("../lib/helpers");
      const pay = getAnswerById(answers, questions, "payment");
      if (!pay) return false;
      if (typeof pay === "string") return pay.toLowerCase() === "insurance";
      return typeof pay === "object" && pay.type === "insurance";
    }
  },
  {
    id: "insurance-info",
    text: "Enter your insurance details.",
    type: "insurance-info",
    optional: false,
    conditional: true,
    dependsOn: "insurance-method",
    dependsOnValue: "id",
  },
  {
    id: "insurance-photos",
    text: "Upload your insurance card.",
    type: "insurance-photos",
    optional: false,
    conditional: true,
    dependsOn: "insurance-method",
    dependsOnValue: "photos",
  },
  {
    id: 'tried',
    text: "What have you tried so far? (Select all that apply)",
    type: 'tried',
  },
  {
    id: 'prescriptions',
    text: "Please list the prescription(s) you've tried.",
    type: 'prescriptions',
    hint: "Add at least one entry, then Continue",
    conditional: true,
    dependsOn: 'tried',
    dependsOnValue: 'Tried prescription(s)'
  },
  {
    id: 'duration',
    text: "How long have you experienced this condition?",
    type: 'duration',
    hint: "Please select one duration option to continue"
  },
  {
    id: 'menstrual-cycle',
    text: "How would you describe your menstrual cycle?",
    type: 'menstrual-cycle',
    hint: "Please select one option to continue",
    conditional: true,
    conditionalFn: ({ answers, questions }) => {
      const sex = getAnswerById(answers, questions, 'sex');
      const pi = getAnswerById(answers, questions, 'personal-info');
      const age = calculateAge(pi?.dateOfBirth);
      return sex === 'Female' && age >= 18;
    },
  },
  {
    id: 'contraceptives',
    text: "Which contraceptives do you use?",
    type: 'contraceptives',
    hint: "Select all that apply",
    conditional: true,
    conditionalFn: ({ answers, questions }) => {
      const sex = getAnswerById(answers, questions, 'sex');
      const pi = getAnswerById(answers, questions, 'personal-info');
      const age = calculateAge(pi?.dateOfBirth);
      return sex === 'Female' && age >= 18;
    },
  },
  {
    id: 'maternity',
    text: "For medication purposes, are you pregnant, trying to get pregnant, breastfeeding, or on birth control?",
    type: 'maternity',
    hint: "You can select multiple responses",
    conditional: true,
    conditionalFn: ({ answers, questions }) => {
      const sex = getAnswerById(answers, questions, 'sex');
      return sex === 'Female'; // all females (minors and adults)
    },
  },
  {
    id: 'has-allergies',
    text: "Do you have any allergies?",
    type: 'has-allergies',
    hint: "Please select Yes or No"
  },
  {
    id: 'allergies',
    text: "Please specify your allergies.",
    type: 'text',
    hint: "Press on the Send button to send your message",
    placeholder: "Please share any allergies — like foods, medications, or supplements.",
    conditional: true,
    conditionalFn: ({ answers, questions }) => {
      const hasAllergies = getAnswerById(answers, questions, "has-allergies");
      // Show allergies question only if user selected "y" (Yes)
      return hasAllergies === "y";
    },
  },
  {
    id: 'credit-card',
    text: "Please enter your credit card information.",
    type: 'credit-card',
    hint: "Enter your card details to complete your intake",
    conditional: true,
    conditionalFn: ({ answers, questions }) => {
      const payment = getAnswerById(answers, questions, "payment");
      if (!payment) return false;
      if (typeof payment === "string") {
        const lc = payment.toLowerCase();
        return lc.includes("insurance") || lc.includes("pocket") || lc.includes("self");
      }
      return typeof payment === "object" && (payment.type === "insurance" || payment.type === "self-pay");
    },
  },
  {
    id: 'photos',
    text: "Please upload photos of your skin issue.",
    type: 'photo-upload',
  },
  {
    id: 'other-meds',
    text: "Are you currently on any other medications, unrelated to the skin issue?",
    type: 'other-meds',
    hint: "Select Yes/No. If Yes, add the medication names."
  },
  {
    id: 'additional',
    text: "Is there anything else you'd like to share? (optional)",
    type: 'text',
    hint: "Press Enter to send your message",
    placeholder: "Type your answer...",
    optional: true
  },
  {
    id: 'address',
    text: "What's your address? (for billing and service purposes)",
    type: 'address',
    hint: "Start typing your address to see suggestions"
  },
  {
    id: 'referral',
    text: "How did you hear about us?",
    type: 'referral',
    hint: "Select one option to continue",
    options: [
      { value: 'google', label: 'Google' },
      { value: 'facebook_instagram', label: 'Facebook/Instagram' },
      { value: 'zocdoc', label: 'Zocdoc or another medical website' },
      { value: 'friend_family', label: 'Friend or family' },
      { value: 'my_doctor', label: 'My doctor' },
      { value: 'already_patient', label: 'Already a patient' },
      { value: 'llm', label: 'LLM' },
      { value: 'other', label: 'Other' }
    ]
  },
  {
    id: "case-id",
    type: "system",
    optional: true,
    hidden: true,
  },
  {
    id: "patient-id",
    type: "system",
    optional: true,
    hidden: true,
  },
];

export const getQuestionType = (questionId) => {
  const question = questions.find(q => q.id === questionId);
  return question ? question.type : 'text';
};
